import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { blocks, i18next, readOnly, textFormatting, view } from '@yola/ws-sdk';
import bowser from 'yola-bowser';
import contextMenu from 'src/js/modules/context-menu';
import highlighter from 'src/js/modules/highlighter';
import commonConstants from 'src/js/modules/common/constants';
import { requestContextMenuReopen } from 'src/js/modules/context-menu/helpers/reopen-context-menu-request';
import BlockHideOnControl from '../components/block-hide-on-control';
import { HIDE_ON_TARGET_TO_BREAKPOINT, HIDE_ON_TARGETS } from '../constants/visibility';
import getCurrentHideOnBreakpoint from '../helpers/get-current-hide-on-breakpoint';
import isHiddenAt from '../helpers/is-hidden-at';
import selectClosestSelectableParent from '../helpers/select-closest-selectable-parent';
import toggleBlockVisibilityAtBreakpoint, {
  getNextClass,
} from '../helpers/toggle-block-visibility-at-breakpoint';
import trackers from '../trackers';

const { RIGHT_CLICK_TRIGGER_ID } = commonConstants.common;

const isHidingAtCurrentBreakpoint = (currentClass, nextClass, currentBreakpoint) =>
  !isHiddenAt(currentClass, currentBreakpoint) && isHiddenAt(nextClass, currentBreakpoint);

function BlockHideOnContainer({ element = null, title, triggerId, isWidget = false }) {
  const elementId = element ? view.accessors.getLiveElementId(element) : null;
  const selection = useSelector(view.selectors.getSelection);
  const [currentClass, setCurrentClass] = useState('');
  const items = [
    { target: HIDE_ON_TARGETS.ALL, glyph: 'all-devices', label: i18next.t('All devices') },
    { target: HIDE_ON_TARGETS.DESKTOP, glyph: 'monitor', label: i18next.t('Desktop') },
    { target: HIDE_ON_TARGETS.TABLET, glyph: 'tablet', label: i18next.t('Tablet') },
    { target: HIDE_ON_TARGETS.MOBILE, glyph: 'smartphone', label: i18next.t('Mobile') },
  ].map((item) => {
    const breakpoint = HIDE_ON_TARGET_TO_BREAKPOINT[item.target];

    return {
      ...item,
      isHidden: isHiddenAt(currentClass, breakpoint),
    };
  });

  const handleItemClick = (target) => {
    const breakpoint = HIDE_ON_TARGET_TO_BREAKPOINT[target];
    const currentBreakpoint = getCurrentHideOnBreakpoint();
    const nextClass = getNextClass(currentClass, breakpoint);
    const hidingAtCurrentBreakpoint = isHidingAtCurrentBreakpoint(
      currentClass,
      nextClass,
      currentBreakpoint
    );

    if (hidingAtCurrentBreakpoint && selection) {
      const advancedSelection = textFormatting.accessors.getAdvancedSelection();
      advancedSelection.removeAllRanges();
      advancedSelection.refresh();
    }

    toggleBlockVisibilityAtBreakpoint(elementId, breakpoint);

    if (readOnly.verifiers.isReadOnlyModeEnabled()) {
      contextMenu.operations.hideContextMenu();
      return;
    }

    if (hidingAtCurrentBreakpoint) {
      const didSelectParent = selectClosestSelectableParent(element);

      if (!bowser.mobile) {
        if (!didSelectParent && !isWidget) {
          view.operations.setSelectedElement(null);
          view.operations.setHoveredElement(null);
          highlighter.operations.hide();
          highlighter.operations.hideAnchors();
        }

        const shouldReopenMenu =
          contextMenu.accessors.getMenu().triggerId !== RIGHT_CLICK_TRIGGER_ID;

        contextMenu.operations.hideContextMenu();

        if (shouldReopenMenu) {
          requestContextMenuReopen();
        }
      }
    }

    setCurrentClass(nextClass);

    trackers.trackBlockVisibilityChanged({
      blockId: blocks.accessors.getBlockIdByElement(element),
      breakpointType: target,
      isHidden: isHiddenAt(nextClass, breakpoint),
      triggerId,
      isWidget,
    });
  };

  useEffect(() => {
    if (!elementId) {
      setCurrentClass('');
      return;
    }

    setCurrentClass(view.accessors.getElementAttribute(elementId, 'class') || '');
  }, [elementId]);

  if (!elementId) {
    return null;
  }

  return <BlockHideOnControl title={title} items={items} onItemClick={handleItemClick} />;
}

BlockHideOnContainer.propTypes = {
  element: PropTypes.oneOfType([PropTypes.instanceOf(Element), PropTypes.object]),
  title: PropTypes.string.isRequired,
  triggerId: PropTypes.string.isRequired,
  isWidget: PropTypes.bool,
};

export default BlockHideOnContainer;
